import React from "react"
import { createPortal } from "react-dom"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Image from "next/image"

import {
  NextLink,
  Icons,
  PriceTypography,
  ConfirmButton,
  CancelButton,
  PromotionBadges
} from "@src/components"
import { useCallback } from "react"
import useInventoryProduct from "./use-product"
import { ROUTES } from "@src/constants/routes"
import useRouterClient from "@src/hooks/use-router-client"
import ProductModel from "@src/models/product"
import { scrollPageTo } from "@src/utils/scrollElement"
import { useSearchParams } from "next/navigation"

import { styled } from "@mui/system"
import { useTranslation } from "react-i18next"
import FormattedPrice from "@src/components/FormattedPrice"
import DangerouslySetInnerHTML from "@src/components/DangerouslySetInnerHTML"
import { MODAL_PRODUCT_ACTIONS_ID } from "@src/components/ProductCard/modal-product-actions"
import { useLayoutContext } from "@src/layouts/provider"

const SizeBox = styled(Box, {
  shouldForwardProp: prop => prop !== "active" && prop !== "disabled"
})(
  ({
    theme,
    active,
    disabled
  }: {
    theme: any
    active: boolean
    disabled: boolean
  }) => ({
    height: 24,
    overflow: "hidden",
    padding: "6px 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    background: "white",
    borderRadius: "16px",
    ...(active && {
      border: "1.5px solid black",
      "& p": {
        fontWeight: 700
      }
    }),
    ...(disabled && {
      opacity: 0.5,
      cursor: "not-allowed",
      pointerEvents: "none"
    })
  })
)

interface Props {
  isMobile: boolean
  isModal: boolean
  detail: ProductModel
  afterPrice?: React.ReactNode
  onOriginalPriceChange?: (value: number) => void
  onSelectedImage: (value: string) => void
  onOpenSizeModal: (e: any) => void
}

const InventoryOption = (props: Props) => {
  const {
    isMobile,
    isModal,
    detail,
    onSelectedImage,
    onOpenSizeModal,
    onOriginalPriceChange,
    afterPrice
  } = props
  const { router } = useRouterClient()
  const searchParams = useSearchParams()
  const { t } = useTranslation()
  const { onSaleColor } = useLayoutContext()
  const stockAnchor = searchParams.get("stockAnchor") || ""

  const {
    loadingWithKey = {},
    currentInventory,
    firstAttributeOptions,
    secondAttributeOptions,
    handleAddToCart,
    handleChangeFirstAttribute,
    handleChangeSecondAttribute,
    onUpdateQuantity,
    count,
    promotionPrice,
    originalPrice
  } = useInventoryProduct(detail, true, false)

  React.useEffect(() => {
    if (originalPrice != null) onOriginalPriceChange?.(originalPrice)
  }, [originalPrice, onOriginalPriceChange])

  const handleScrollToBrachStock = () => {
    if (!document) return
    const stockElement = document.getElementById("branch_stock")
    if (stockElement) {
      const anchorY =
        stockElement.getBoundingClientRect().top + window.scrollY - 20
      scrollPageTo(anchorY, 200, () => {
        // clear anchorStock after scroll and keep other params
        const params = new URLSearchParams(window.location.search)
        params.delete("stockAnchor")
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}?${params.toString()}`
        )
      })
    } else {
      const params = new URLSearchParams(window.location.search)
      params.delete("stockAnchor")
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${params.toString()}`
      )
    }
  }

  React.useEffect(() => {
    if (stockAnchor === "true") {
      setTimeout(() => {
        handleScrollToBrachStock()
      }, 200)
    }
  }, [stockAnchor])

  const handleChangeColor = useCallback(
    (value: string) => {
      handleChangeFirstAttribute(value)
      const firstAttribute = firstAttributeOptions?.options.find(
        option => option.value === value
      )
      if (firstAttribute?.image) {
        onSelectedImage(firstAttribute.image)
      }
    },
    [handleChangeFirstAttribute]
  )

  const handleBuyNow = () => {
    handleAddToCart({
      showToast: true,
      onSuccess: () => {
        router.push(ROUTES.CART)
      },
      loadingKey: "buyNow"
    })
  }

  const isDisabledAddToCart =
    count < 1 || currentInventory?.stock_quantity < 1 || !currentInventory?.id

  const isCompactModal = isModal && isMobile
  const sectionGap = isCompactModal ? 1 : 2
  const [actionsHost, setActionsHost] = React.useState<HTMLElement | null>(null)

  React.useEffect(() => {
    if (!isCompactModal) {
      setActionsHost(null)
      return
    }
    setActionsHost(document.getElementById(MODAL_PRODUCT_ACTIONS_ID))
  }, [isCompactModal])

  const modalActionButtons = (
    <Stack
      justifyContent="center"
      gap={1.5}
      flexDirection="row"
      sx={{ width: "100%" }}
    >
      <CancelButton
        minHeightMd={40}
        minWidth={isCompactModal ? 0 : 160}
        label={t("add_to_cart")}
        sx={{
          flex: 1,
          span: {
            textTransform: "none",
            fontSize: isCompactModal ? 13 : undefined
          }
        }}
        disabled={isDisabledAddToCart}
        loading={loadingWithKey?.addCart}
        onClick={handleAddToCart}
      />
      <ConfirmButton
        minHeightMd={40}
        minWidth={isCompactModal ? 0 : 240}
        label={t("buy_now")}
        sx={{
          flex: 1,
          span: {
            textTransform: "none",
            fontSize: isCompactModal ? 13 : undefined
          }
        }}
        disabled={isDisabledAddToCart}
        loading={loadingWithKey?.buyNow}
        onClick={handleBuyNow}
      />
    </Stack>
  )

  return (
    <Stack
      flexDirection="column"
      gap={{
        xs: isCompactModal ? 1 : 1.5,
        md: 2
      }}
      width="100%"
      pb={isCompactModal ? 1.5 : 0}
    >
      <Typography
        fontSize={isMobile ? 14 : 20}
        fontWeight={700}
        lineHeight={isCompactModal ? 1.35 : "30px"}
        sx={
          isCompactModal
            ? {
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }
            : undefined
        }
      >
        {detail?.name || ""}
      </Typography>
      {!isModal &&
        (() => {
          const specItems =
            detail?.spec_attributes?.filter(
              item =>
                item && (item.label || item.value) && item.visible !== false
            ) ?? []

          if (!specItems.length) return null

          return (
            <Box
              component="dl"
              sx={{
                m: 0,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: { xs: 2, sm: 3 },
                rowGap: { xs: 1.25, sm: 1.5 },
                pt: { xs: 0.5, md: 0.75 },
                pb: { xs: 0.5, md: 0.75 },
                "@keyframes productSpecIn": {
                  from: { opacity: 0, transform: "translateY(6px)" },
                  to: { opacity: 1, transform: "translateY(0)" }
                },
                "& > div": {
                  minWidth: 0,
                  animation: "productSpecIn 420ms ease both"
                },
                "& > div:nth-of-type(n)": {},
                "@media (prefers-reduced-motion: reduce)": {
                  "& > div": { animation: "none" }
                }
              }}
            >
              {specItems.map((item, index) => (
                <Box
                  key={`${item.label || "spec"}-${index}`}
                  component="div"
                  sx={{ animationDelay: `${40 + index * 60}ms` }}
                >
                  <Typography
                    component="dt"
                    sx={{
                      m: 0,
                      mb: 0.5,
                      fontSize: { xs: 10, md: 11 },
                      fontWeight: 500,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: "rgba(0,0,0,0.45)",
                      lineHeight: 1.2
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    component="dd"
                    sx={{
                      m: 0,
                      fontSize: { xs: 13, md: 15 },
                      fontWeight: 500,
                      color: "#000",
                      lineHeight: 1.35,
                      wordBreak: "break-word"
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )
        })()}
      {(() => {
        const hasPromotion = Boolean(
          promotionPrice && originalPrice && promotionPrice < originalPrice
        )
        const calcPercent =
          hasPromotion && originalPrice > 0
            ? Math.round(((originalPrice - promotionPrice) / originalPrice) * 100)
            : 0

        return (
          <Stack
            gap={isCompactModal ? 1 : 1.5}
            direction="row"
            alignItems="baseline"
            flexWrap="wrap"
          >
            <Typography
              fontSize={isMobile ? 16 : 20}
              fontWeight={700}
              lineHeight="30px"
              sx={{
                color: hasPromotion ? onSaleColor || "#D8042C" : "#171717",
                fontVariantNumeric: "tabular-nums"
              }}
            >
              <FormattedPrice price={hasPromotion ? promotionPrice : originalPrice} />
            </Typography>
            <PriceTypography
              fontSize={isMobile ? 14 : 16}
              fontWeight={600}
              lineHeight="30px"
              isNull={!hasPromotion}
            >
              <FormattedPrice price={originalPrice} />
            </PriceTypography>
            {hasPromotion && calcPercent > 0 && (
              <Typography
                component="span"
                sx={{
                  fontSize: isMobile ? 11 : 12,
                  fontWeight: 700,
                  color: onSaleColor || "#D8042C",
                  bgcolor: "#FFF0F1",
                  border: "1px solid rgba(216, 4, 44, 0.2)",
                  borderRadius: "4px",
                  px: 0.75,
                  py: 0.15,
                  lineHeight: 1.2
                }}
              >
                -{calcPercent}%
              </Typography>
            )}
          </Stack>
        )
      })()}
      {afterPrice}
      <PromotionBadges promotions={detail?.promotions} size="md" />
      {firstAttributeOptions?.options.length > 0 && (
        <Stack flexDirection="column" gap={sectionGap}>
          <Stack gap={1} direction="row" alignItems="center" flexWrap="wrap">
            <Typography
              variant={isMobile ? "body1" : "body"}
              fontWeight={600}
              lineHeight="24px"
            >
              {firstAttributeOptions.name}:
            </Typography>
            <Typography
              variant={isMobile ? "body1" : "body"}
              fontWeight={400}
              lineHeight="24px"
            >
              {currentInventory?.firstAttribute.value}
            </Typography>
          </Stack>
          <Stack gap={1.5} flexWrap="wrap" direction="row">
            {firstAttributeOptions?.options.map((item, index) => (
              <Box
                key={index}
                width={isModal ? 40 : 58}
                height={isModal ? 40 : 58}
                borderRadius="8px"
                overflow="hidden"
                sx={{
                  cursor: "pointer",
                  ...(currentInventory?.firstAttribute?.value ===
                    item.value && {
                    border: "1px solid black"
                  })
                }}
                role="button"
                onClick={() => handleChangeColor(item.value)}
              >
                <Image
                  width={isModal ? 40 : 58}
                  height={isModal ? 40 : 58}
                  src={
                    item.small_image ||
                    detail?.images?.[0] ||
                    "/assets/images/no-image.png"
                  }
                  style={{ objectFit: "cover", objectPosition: "center" }}
                  alt=""
                />
              </Box>
            ))}
          </Stack>
        </Stack>
      )}
      {secondAttributeOptions?.options?.length > 0 && (
        <Stack flexDirection="column" gap={sectionGap}>
          <Typography
            variant={isMobile ? "body1" : "body"}
            fontWeight={600}
            lineHeight="24px"
          >
            Size
          </Typography>
          <Stack gap={1.5} direction="row" flexWrap="wrap">
            {secondAttributeOptions?.options?.map((item, index) => (
              <SizeBox
                key={index}
                active={currentInventory?.secondAttribute?.value === item.value}
                onClick={() => handleChangeSecondAttribute(item.value)}
                // disabled={item?.stock_quantity == 0}
              >
                <Typography
                  variant="body1"
                  fontWeight={400}
                  lineHeight="24px"
                  textTransform="uppercase"
                >
                  {item?.value}
                </Typography>
              </SizeBox>
            ))}
          </Stack>
          <Box
            component="button"
            type="button"
            onClick={onOpenSizeModal}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              p: 0,
              m: 0,
              border: 0,
              background: "none",
              cursor: "pointer",
              color: "black",
              fontSize: isCompactModal ? "13px" : "16px",
              textDecoration: "underline",
              fontFamily: "inherit"
            }}
          >
            <Typography
              variant={isMobile ? "body1" : "body"}
              fontSize={isCompactModal ? 13 : undefined}
            >
              {t("size_selection_guide")}
            </Typography>

            <Icons
              sx={{
                marginTop: "-4px"
              }}
              name="caret-right"
            />
          </Box>
        </Stack>
      )}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
      >
        <Typography fontSize="12px" color="#707070">
          {t("stock_quantity")}: {currentInventory?.stock_quantity || 0}
        </Typography>
        {currentInventory?.stock_quantity < 1 && (
          <Typography
            variant="body1"
            fontWeight={400}
            lineHeight="24px"
            color="red"
          >
            {t("out_of_stock")}
          </Typography>
        )}
      </Stack>
      <Stack gap={sectionGap} direction="row" alignItems="center">
        <Typography variant={isMobile ? "body1" : "body"}>
          {t("quantity")}
        </Typography>
        <Stack gap={1} direction="row" alignItems="center">
          <Button
            sx={{
              border: "none",
              padding: "0px",
              color: "black",
              minWidth: "32px",
              minHeight: "24px",
              backgroundColor: "transparent",
              "&.Mui-disabled": {
                opacity: 0.5,
                pointerEvents: "none"
              }
            }}
            disabled={count <= 1 || currentInventory?.stock_quantity <= 1}
            onClick={() => {
              onUpdateQuantity(count - 1)
            }}
          >
            <svg
              width="14"
              height="2"
              viewBox="0 0 16 2"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1.25H15"
                stroke="black"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
          <SizeBox
            sx={{
              border: "1.5px solid black",
              borderRadius: "16px",
              minWidth: 48
            }}
          >
            <span>{count}</span>
          </SizeBox>
          <Button
            sx={{
              border: "none",
              padding: "0px",
              color: "black",
              minWidth: "32px",
              minHeight: "24px",
              backgroundColor: "transparent",
              "&.Mui-disabled": {
                opacity: 0.5,
                pointerEvents: "none"
              }
            }}
            disabled={
              count < 1 ||
              currentInventory?.stock_quantity <= 1 ||
              currentInventory?.stock_quantity === count
            }
            onClick={() => {
              onUpdateQuantity(count + 1)
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 1.25V15.25M1 8.25H15"
                stroke="black"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        </Stack>
      </Stack>
      {/* Hidden Modal of Mobile */}
      {!isModal && (
        <Stack
          sx={{
            position: "relative",
            backgroundColor: "transparent",
            padding: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99,
            width: "100%",
            margin: "0px auto"
          }}
          gap={2}
        >
          <CancelButton
            minHeightMd={40}
            minWidth={160}
            label={t("add_to_cart")}
            sx={{
              span: {
                textTransform: "none"
              }
            }}
            disabled={isDisabledAddToCart}
            loading={loadingWithKey?.addCart}
            onClick={handleAddToCart}
          />
          <ConfirmButton
            minHeightMd={40}
            minWidth={220}
            minWidthMd="50%"
            label={t("buy_now")}
            sx={{
              span: {
                textTransform: "none"
              }
            }}
            disabled={isDisabledAddToCart}
            loading={loadingWithKey?.buyNow}
            onClick={handleBuyNow}
          />
        </Stack>
      )}

      {!isModal && (
        <Stack
          alignItems="center"
          gap="4px"
          onClick={handleScrollToBrachStock}
          role="button"
          sx={{
            marginLeft: "-4px",
            cursor: "pointer"
          }}
        >
          <Icons name="location_black" />
          <Typography
            variant="body1"
            fontSize={14}
            fontWeight={500}
            sx={{
              textDecoration: "underline",
              color: "#000000",
              "&:hover": {
                color: "#000000",
                opacity: 0.6
              }
            }}
          >
            {t("available_stock_address")}
          </Typography>
        </Stack>
      )}

      {isModal && (
        <>
          <Stack
            gap={1}
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            sx={{ pt: 0.5 }}
          >
            <NextLink
              href={`${ROUTES.PRODUCT}/${detail?.slug}?stockAnchor=true`}
              passHref
              sx={{
                marginLeft: "-4px",
                textDecoration: "underline",
                "&:hover p": {
                  color: "#000000",
                  opacity: 0.6
                }
              }}
            >
              <Stack alignItems="center" gap="4px">
                <Icons name="location_black" />
                <Typography variant="body1" fontSize={13} fontWeight={500}>
                  {t("available_stock_address")}
                </Typography>
              </Stack>
            </NextLink>
            <NextLink
              style={{
                color: "#87BD5E",
                fontSize: "13px",
                fontWeight: 600,
                textDecoration: "underline"
              }}
              href={`${ROUTES.PRODUCT}/${detail?.slug}`}
              passHref
            >
              {t("see_full_details")}
            </NextLink>
          </Stack>
          {isCompactModal ? (
            actionsHost ? (
              createPortal(modalActionButtons, actionsHost)
            ) : null
          ) : (
            <Stack
              sx={{
                padding: "8px 0px",
                zIndex: 99,
                width: "100%",
                margin: "auto"
              }}
              justifyContent="center"
              gap={2}
              flexDirection="row"
            >
              <CancelButton
                minHeightMd={40}
                minWidth={160}
                label={t("add_to_cart")}
                sx={{
                  span: {
                    textTransform: "none"
                  }
                }}
                disabled={isDisabledAddToCart}
                loading={loadingWithKey?.addCart}
                onClick={handleAddToCart}
              />
              <ConfirmButton
                minHeightMd={40}
                minWidth={240}
                label={t("buy_now")}
                sx={{
                  span: {
                    textTransform: "none"
                  }
                }}
                disabled={isDisabledAddToCart}
                loading={loadingWithKey?.buyNow}
                onClick={handleBuyNow}
              />
            </Stack>
          )}
        </>
      )}
    </Stack>
  )
}

export default InventoryOption
